import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../../../services/api'
import { useAccount } from '../../../hooks/useAccount'
import ProductGrid from './ProductGrid'
import ProductFilters from './ProductFilters'

function ProductManager() {
  const { t } = useTranslation('ecommerce')
  const navigate = useNavigate()
  const { selectedAccount } = useAccount()

  const [products, setProducts] = useState([])
  const [allProducts, setAllProducts] = useState([]) // For extracting brands/categories
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [filters, setFilters] = useState({
    search: '',
    is_featured: null,
    inventory_status: null,
    has_discount: null,
    has_rating: null,
    min_rating: null,
    max_stock: null,
    brand: null,
    category: null,
  })

  // Extract unique brands and categories from products
  const availableBrands = useMemo(() => {
    const brands = new Set()
    allProducts.forEach((p) => {
      if (p.brand) brands.add(p.brand)
    })
    return Array.from(brands).sort()
  }, [allProducts])

  const availableCategories = useMemo(() => {
    const categories = new Set()
    allProducts.forEach((p) => {
      if (p.category) categories.add(p.category)
    })
    return Array.from(categories).sort()
  }, [allProducts])

  const fetchProducts = useCallback(async () => {
    if (!selectedAccount?.id) return

    try {
      setLoading(true)
      setError(null)

      const params = {
        instagram_account_id: selectedAccount.id,
        ...(filters.search && { search: filters.search }),
        ...(filters.is_featured !== null && { is_featured: filters.is_featured }),
        ...(filters.inventory_status && { inventory_status: filters.inventory_status }),
        ...(filters.has_discount !== null && { has_discount: filters.has_discount }),
        ...(filters.has_rating !== null && { has_rating: filters.has_rating }),
        ...(filters.min_rating !== null && { min_rating: filters.min_rating }),
        ...(filters.max_stock !== null && { max_stock: filters.max_stock }),
        ...(filters.brand && { brand: filters.brand }),
        ...(filters.category && { category: filters.category }),
      }

      const result = await api.getProducts(params)
      setProducts(result.products || [])

      // Also fetch all products once for brand/category extraction (without filters)
      if (allProducts.length === 0) {
        const allResult = await api.getProducts({ instagram_account_id: selectedAccount.id, limit: 500 })
        setAllProducts(allResult.products || [])
      }
    } catch (err) {
      console.error('Failed to fetch products:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [selectedAccount?.id, filters, allProducts.length])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  const handleCreateStories = useCallback(() => {
    if (selectedIds.length === 0) return
    navigate('/ecommerce/create-stories', {
      state: { productIds: selectedIds },
    })
  }, [selectedIds, navigate])

  // Products with video_url among the selected ones
  const selectedWithVideo = useMemo(() => {
    return products.filter((p) => selectedIds.includes(p.product_id) && p.video_url)
  }, [products, selectedIds])

  const [importingVideos, setImportingVideos] = useState(false)

  const handleUseExistingVideo = useCallback(async () => {
    if (selectedWithVideo.length === 0) return
    try {
      setImportingVideos(true)
      setError(null)
      for (const product of selectedWithVideo) {
        await api.useExistingVideo(product.product_id)
      }
      setSelectedIds([])
      navigate('/ecommerce/proposals')
    } catch (err) {
      console.error('Failed to import existing videos:', err)
      setError(err.message)
    } finally {
      setImportingVideos(false)
    }
  }, [selectedWithVideo, navigate])

  const handleToggleFeatured = useCallback(async () => {
    if (selectedIds.length === 0) return
    try {
      await api.bulkUpdateProducts(selectedIds, { is_featured: true })
      fetchProducts()
      setSelectedIds([])
    } catch (err) {
      setError(err.message)
    }
  }, [selectedIds, fetchProducts])

  // Stats summary
  const stats = useMemo(() => {
    const totalClicks = products.reduce((sum, p) => sum + (p.click_count || 0), 0)
    const totalStories = products.reduce((sum, p) => sum + (p.story_count || 0), 0)
    const withRatings = products.filter((p) => p.aggregate_rating).length
    const lowStock = products.filter((p) => p.stock_count && p.stock_count <= 10).length
    return { totalClicks, totalStories, withRatings, lowStock }
  }, [products])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('products.title', 'Products')}
          </h1>
          <p className="mt-1 text-gray-600">
            {t('products.subtitle', 'Select products to create stories')}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/ecommerce/feeds')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('products.manageFeed', 'Manage Feed')}
          </button>
          {selectedIds.length > 0 && (
            <>
              {selectedWithVideo.length > 0 && (
                <button
                  onClick={handleUseExistingVideo}
                  disabled={importingVideos}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {importingVideos
                    ? t('products.importingVideos', 'Importing...')
                    : `${t('products.useExistingVideo', 'Use Existing Video')} (${selectedWithVideo.length})`}
                </button>
              )}
              <button
                onClick={handleToggleFeatured}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('products.markFeatured', 'Mark Featured')}
              </button>
              <button
                onClick={handleCreateStories}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
              >
                {t('products.createStories', 'Create Stories')} ({selectedIds.length})
              </button>
            </>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      {products.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{products.length}</div>
            <div className="text-sm text-gray-500">{t('products.stats.total', 'Products')}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.totalClicks.toLocaleString()}</div>
            <div className="text-sm text-gray-500">{t('products.stats.clicks', 'Bio Clicks')}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.totalStories}</div>
            <div className="text-sm text-gray-500">{t('products.stats.stories', 'Stories Created')}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.withRatings}</div>
            <div className="text-sm text-gray-500">{t('products.stats.withRatings', 'With Ratings')}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <ProductFilters
        filters={filters}
        onChange={setFilters}
        availableBrands={availableBrands}
        availableCategories={availableCategories}
      />

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
          <button
            onClick={fetchProducts}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            {t('common.retry', 'Retry')}
          </button>
        </div>
      )}

      {/* Product Grid */}
      <ProductGrid
        products={products}
        loading={loading}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />
    </div>
  )
}

export default ProductManager
